// The staleness window the roster chips filter on, shared by every panel and
// persisted across reloads. A module store rather than component state because
// the dock and the Messages inbox can be on screen at once, and a coach who
// switches the dock to 30 days does not expect the inbox to still say 10.
//
// The threshold is never sent to the API — the payload carries dates, so
// switching 10 → 30 re-filters instantly with no network traffic.

import { THRESHOLD_OPTIONS, DEFAULT_THRESHOLD_DAYS } from "./insightDerive";

const STORAGE_KEY = "coachInsightPrefs";

const listeners = new Set();

/** Only a value the chips can actually display; anything else falls back. */
const normalize = (value) => {
  const days = Number(value);
  return THRESHOLD_OPTIONS.includes(days) ? days : DEFAULT_THRESHOLD_DAYS;
};

const readStored = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_THRESHOLD_DAYS;
    return normalize(JSON.parse(raw)?.thresholdDays);
  } catch (err) {
    return DEFAULT_THRESHOLD_DAYS;
  }
};

let thresholdDays = readStored();

/** @returns {number} */
export function getThresholdDays() {
  return thresholdDays;
}

/**
 * @param {() => void} listener
 * @returns {() => void} Unsubscribe.
 */
export function subscribeInsightPrefs(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * @param {number} value - One of THRESHOLD_OPTIONS; anything else resets to the default.
 */
export function setThresholdDays(value) {
  const next = normalize(value);
  if (next === thresholdDays) return;

  thresholdDays = next;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ thresholdDays: next }));
  } catch (err) {
    // A full or blocked localStorage costs persistence, not the feature.
    console.error("insightPrefs: failed to persist threshold", err);
  }
  listeners.forEach((listener) => listener());
}
